/**
 * Love Entity Types — Sprint 16 Redesign
 *
 * Concrete entity detection replaces abstract dimension scoring.
 * Entities represent specific things, experiences, and people a user loves,
 * backed by behavioral evidence from raw data sources.
 */
// ---------------------------------------------------------------------------
// Factory helpers
// ---------------------------------------------------------------------------
/** Create a deterministic entity ID */
export function makeLoveEntityId(type, name) {
    const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    // Simple hash: sum of char codes (deterministic, fast)
    let hash = 0;
    for (let i = 0; i < normalized.length; i++) {
        hash = ((hash << 5) - hash + normalized.charCodeAt(i)) | 0;
    }
    return `love-${type}-${Math.abs(hash).toString(36)}`;
}
/** Create an empty source breakdown */
export function emptySourceBreakdown() {
    return { email: 0, financial: 0, calendar: 0, browsing: 0 };
}
/** Create an empty proxy breakdown */
export function emptyProxyBreakdown() {
    return { attention: 0, investment: 0, communication: 0, repetition: 0, initiative: 0, priority: 0 };
}
/** Create a new LoveEntity from initial signals */
export function createLoveEntity(name, type, category, initialSignals, confidence) {
    const now = Date.now();
    const sourceBreakdown = emptySourceBreakdown();
    const proxyBreakdown = emptyProxyBreakdown();
    for (const signal of initialSignals) {
        sourceBreakdown[signal.source]++;
        proxyBreakdown[signal.proxy]++;
    }
    return {
        id: makeLoveEntityId(type, name),
        type,
        name,
        category,
        subcategories: [],
        confidence,
        lastValidatedAt: now,
        signals: initialSignals.slice(0, 20),
        sourceBreakdown,
        proxyBreakdown,
        firstSeenAt: now,
        lastSignalAt: initialSignals.length > 0
            ? Math.max(...initialSignals.map(s => s.timestamp))
            : now,
        trend: 'new',
        actionableContext: { suggestedActions: [], relatedEntityIds: [] },
        reasoning: '',
        proxyEvidence: {},
        ikigaiRelevance: { love: 0, goodAt: 0, worldNeeds: 0, paidFor: 0 },
        stability: 0.5,
        pass1Model: '',
        pass2Model: '',
        detectionRunId: '',
    };
}
//# sourceMappingURL=love-entity.js.map